import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  CheckCircle, 
  AlertTriangle, 
  Building2, 
  User, 
  Mail, 
  Phone,
  MapPin,
  Globe,
  Target,
  DollarSign,
  FileCheck,
  Send,
  Loader2,
  BarChart3,
  Headphones,
  ArrowLeft
} from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Form Options
const COUNTRIES = [
  "United States",
  "Canada", 
  "United Kingdom",
  "Other"
];

const VERTICALS = [
  "Plumbers",
  "HVAC (Air Conditioning)",
  "Heating Contractors",
  "Roofers",
  "Electricians",
  "Painters",
  "Bathroom Remodeling",
  "Appliance Repair",
  "Movers",
  "Junk Removal",
  "Water & Fire Damage Restoration",
  "Windows Installers",
  "Solar Installation",
  "Exterminators (Pest Control)",
  "Garage Door Services",
  "Gutters",
  "Locksmith",
  "Chimney Services",
  "Air Duct Cleaning",
  "Dumpster Rentals",
  "Flight Booking"
];

const DAILY_VOLUMES = [
  "10 – 25 Calls",
  "25 – 50 Calls",
  "50 – 100 Calls",
  "100 – 250 Calls",
  "250 – 500 Calls",
  "500+ Calls"
];

const BUDGET_RANGES = [
  "$10 – $25 per call",
  "$25 – $50 per call",
  "$50 – $100 per call",
  "$100 – $200 per call",
  "$200+ per call",
  "Negotiable based on quality"
];

const CALL_PLATFORMS = [
  "Ringba",
  "Retreaver",
  "CallRail",
  "Invoca",
  "Five9",
  "Other",
  "None - Need Recommendation"
];

const TARGET_GEOS = [
  "USA Nationwide",
  "Specific States",
  "Specific Cities",
  "Canada",
  "Multiple Countries"
];

const CALL_TYPES = [
  "Exclusive Calls Only",
  "Shared Calls Okay",
  "Both - Flexible"
];

// Components
const SectionTitle = ({ icon: Icon, title, number }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
      {number}
    </div>
    <Icon className="w-5 h-5 text-blue-600" />
    <h3 className="font-['Poppins'] text-lg font-semibold text-slate-800">{title}</h3>
  </div>
);

const InputField = ({ label, required, ...props }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800"
      {...props}
    />
  </div>
);

const SelectField = ({ label, required, options, ...props }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800 bg-white"
      {...props}
    >
      <option value="">Select an option</option>
      {options.map((opt, i) => (
        <option key={i} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

const MultiSelect = ({ label, required, options, selected, onChange }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {options.map((opt, i) => (
        <label
          key={i}
          className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-all text-sm ${
            selected.includes(opt)
              ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white border-blue-600"
              : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
          }`}
        >
          <input
            type="checkbox"
            checked={selected.includes(opt)}
            onChange={(e) => {
              if (e.target.checked) {
                onChange([...selected, opt]);
              } else {
                onChange(selected.filter(s => s !== opt));
              }
            }}
            className="sr-only"
          />
          <CheckCircle className={`w-4 h-4 flex-shrink-0 ${selected.includes(opt) ? "opacity-100" : "opacity-0"}`} />
          <span className="truncate">{opt}</span>
        </label>
      ))}
    </div>
  </div>
);

// Main Component
const BuyerApplicationPage = () => {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    website: "",
    country: "",
    verticals: [],
    target_geo: "",
    specific_states: "",
    daily_call_volume: "",
    budget_per_call: "",
    call_type_preference: "",
    call_platform: "",
    current_call_sources: "",
    additional_requirements: "",
    agreed_to_terms: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API_URL}/api/buyer/apply`, formData);
      setSubmitStatus({ type: "success", message: response.data.message, id: response.data.application_id });
    } catch (error) {
      console.error("Form submission error:", error.response?.data || error.message);
      let errorMessage = "Failed to submit application. Please try again.";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => `${e.loc?.join('.')} - ${e.msg}`).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      setSubmitStatus({ 
        type: "error", 
        message: errorMessage 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus?.type === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-blue-600" />
          </div>
          <h2 className="font-['Poppins'] text-2xl font-bold text-slate-800 mb-4">Application Submitted!</h2>
          <p className="text-slate-600 mb-6">
            Thank you for your interest in buying calls from Digital Master Media LLC.<br /><br />
            Our team will review your application and contact you within 24-48 hours.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Application ID: <span className="font-mono">{submitStatus.id}</span>
          </p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-6 py-3 rounded-full hover:shadow-lg transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Homepage
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 font-['Inter']">
      {/* Header */}
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] text-white py-12 px-4 relative overflow-hidden pt-28">
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm mb-4 border border-white/10">
            <Building2 className="w-4 h-4" />
            Buyer Application
          </div>
          <h1 className="font-['Poppins'] text-3xl sm:text-4xl font-bold mb-3">
            Buy High-Quality <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Inbound Calls</span>
          </h1>
          <p className="text-white/80 text-sm sm:text-base max-w-xl mx-auto">
            Get exclusive access to high-intent customer calls for your business. Fill out the form below to get started.
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto p-4 sm:p-6 -mt-6" data-testid="buyer-form">
        {/* Section 1: Company Information */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
          <SectionTitle icon={Building2} title="Company Information" number="1" />
          
          <div className="grid sm:grid-cols-2 gap-4">
            <InputField
              label="Company Name"
              required
              type="text"
              value={formData.company_name}
              onChange={(e) => handleChange("company_name", e.target.value)}
              placeholder="Your Company LLC"
            />
            <InputField
              label="Contact Person"
              required
              type="text"
              value={formData.contact_person}
              onChange={(e) => handleChange("contact_person", e.target.value)}
              placeholder="John Doe"
            />
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <InputField
              label="Email Address"
              required
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="john@company.com"
            />
            <InputField
              label="Phone Number"
              required
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="+1 (555) 123-4567"
            />
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <InputField
              label="Company Website"
              type="url"
              value={formData.website}
              onChange={(e) => handleChange("website", e.target.value)}
              placeholder="https://www.yourcompany.com"
            />
            <SelectField
              label="Country"
              required
              options={COUNTRIES}
              value={formData.country}
              onChange={(e) => handleChange("country", e.target.value)}
            />
          </div>
        </div>

        {/* Section 2: Call Requirements */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <SectionTitle icon={Target} title="Call Requirements" number="2" />
          
          <MultiSelect
            label="Which verticals do you want to buy calls for?"
            required
            options={VERTICALS}
            selected={formData.verticals}
            onChange={(val) => handleChange("verticals", val)}
          />
        </div>

        {/* Section 3: Geographic Targeting */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <SectionTitle icon={Globe} title="Geographic Targeting" number="3" />
          
          <SelectField
            label="Target Geographic Area"
            required
            options={TARGET_GEOS}
            value={formData.target_geo}
            onChange={(e) => handleChange("target_geo", e.target.value)}
          />
          
          {(formData.target_geo === "Specific States" || formData.target_geo === "Specific Cities") && (
            <InputField
              label="Please specify states/cities"
              required
              type="text"
              value={formData.specific_states}
              onChange={(e) => handleChange("specific_states", e.target.value)}
              placeholder="e.g., California, Texas, Florida or Los Angeles, Houston, Miami"
            />
          )}
        </div>

        {/* Section 4: Volume & Budget */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <SectionTitle icon={BarChart3} title="Volume & Budget" number="4" />
          
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField
              label="Daily Call Volume Needed"
              required
              options={DAILY_VOLUMES}
              value={formData.daily_call_volume}
              onChange={(e) => handleChange("daily_call_volume", e.target.value)}
            />
            <SelectField
              label="Budget Per Call"
              required
              options={BUDGET_RANGES}
              value={formData.budget_per_call}
              onChange={(e) => handleChange("budget_per_call", e.target.value)}
            />
          </div>
          
          <SelectField
            label="Call Type Preference"
            required
            options={CALL_TYPES}
            value={formData.call_type_preference}
            onChange={(e) => handleChange("call_type_preference", e.target.value)}
          />
        </div>

        {/* Section 5: Technical Setup */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <SectionTitle icon={Headphones} title="Technical Setup" number="5" />
          
          <SelectField
            label="Call Tracking Platform"
            required
            options={CALL_PLATFORMS}
            value={formData.call_platform}
            onChange={(e) => handleChange("call_platform", e.target.value)}
          />
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Current Call Sources (if any)
            </label>
            <textarea
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all text-slate-800 min-h-[80px]"
              value={formData.current_call_sources}
              onChange={(e) => handleChange("current_call_sources", e.target.value)}
              placeholder="e.g., Currently buying from XYZ network, running our own Google Ads, etc."
            />
          </div>
        </div>

        {/* Section 6: Additional Information */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <SectionTitle icon={FileCheck} title="Additional Information" number="6" />
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Any specific requirements or questions?
            </label>
            <textarea
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all text-slate-800 min-h-[100px]"
              value={formData.additional_requirements}
              onChange={(e) => handleChange("additional_requirements", e.target.value)}
              placeholder="Tell us about your call handling capacity, hours of operation, specific quality requirements, etc."
            />
          </div>
        </div>

        {/* Agreement */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.agreed_to_terms}
              onChange={(e) => handleChange("agreed_to_terms", e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-slate-300 text-green-500 focus:ring-green-500"
            />
            <span className="text-slate-700 text-sm">
              I agree to the <strong>Digital Master Media LLC Buyer Terms and Conditions</strong>. I understand that call pricing and availability are subject to negotiation.
            </span>
          </label>
        </div>

        {/* Error Message */}
        {submitStatus?.type === "error" && (
          <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-700 text-sm">{submitStatus.message}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || !formData.agreed_to_terms}
          data-testid="buyer-submit-btn"
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-400 disabled:to-slate-400 text-white font-bold text-lg py-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Submit Buyer Application
            </>
          )}
        </button>

        <p className="text-center text-slate-500 text-xs mt-4">
          Our team will contact you within 24-48 hours to discuss your requirements.
        </p>
      </form>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BuyerApplicationPage;
